import { useQuery } from '@tanstack/react-query';
import { GlobalMessage, GlobalMessageIntent, StrapiResponse } from 'types/cms';

export const GLOBAL_MESSAGE_KEY = 'globalMessage';
const INTENT_ORDER: { [K in GlobalMessageIntent]: number } = {
  outage: 1,
  warning: 2,
  marketing: 3,
};

function getGlobalMessages(locale: string): Promise<GlobalMessage[]> {
  const normalizedLocale = locale.startsWith('fr') ? 'fr' : 'en';
  const stateFilter =
    import.meta.env.VITE_ENVIRONMENT === 'production'
      ? 'filters[environment][$eq]=prod'
      : 'filters[environment][$eq]=recette';
  const requestURL = `${
    import.meta.env.VITE_CMS_API_URL
  }/messages?locale=${normalizedLocale}&${stateFilter}&populate=*&sort=intent:desc`;

  return fetch(requestURL).then(async res => {
    if (!res.ok) {
      throw new Error('Failed to fetch global notifications');
    }
    const body: StrapiResponse<GlobalMessage> = await res.json();
    return body.data;
  });
}

export function filterAndSortGlobalMessages(
  messages: GlobalMessage[],
  options?: {
    excludeIntents?: GlobalMessageIntent[];
    userPlan?: string;
  },
): GlobalMessage | undefined {
  const filteredMessages = messages
    .filter(m => !options?.excludeIntents?.includes(m.intent))
    .filter(m => {
      if (!m.targetPlan) return true; // do not filter if target plan is empty
      return m.targetPlan === options?.userPlan;
    })
    // Sort by severity (outage > warning > marketing)
    .sort((a, b) => INTENT_ORDER[a.intent] - INTENT_ORDER[b.intent]);

  // Return the highest priority message or undefined if no messages match
  return filteredMessages.length > 0 ? filteredMessages[0] : undefined;
}

export function useGlobalMessage(
  locale: string,
  options?: {
    excludeIntents?: GlobalMessageIntent[];
    userPlan?: string;
  },
): GlobalMessage | undefined {
  const { data: globalMessages = [] } = useQuery({
    queryKey: [GLOBAL_MESSAGE_KEY, locale],
    queryFn: () => getGlobalMessages(locale),
    refetchInterval: 1000 * 60 * 5, // 5 minutes
    staleTime: 1000 * 60 * 3, // 3 minutes
    refetchOnWindowFocus: false,
  });

  return filterAndSortGlobalMessages(globalMessages, options);
}
